import React from 'react';
import {
  Typography,
  Box,
  Grid,
  Button,
  useTheme,
} from '@mui/material';
import { PageLayout } from '../../components/footer/PageLayout';
import { ContentSection, FeatureCard, StatsDisplay } from '../../components/footer/ContentSection';
import { CTASection } from '../../components/footer/CTASection';

export const AboutPage: React.FC = () => {
  const theme = useTheme();

  const values = [
    {
      icon: "🤝",
      title: "Quality Care",
      description: "We believe everyone deserves access to high-quality healthcare services delivered by qualified professionals.",
    },
    {
      icon: "🔒",
      title: "Safety First",
      description: "Patient safety is our top priority. We ensure all caregivers are thoroughly vetted and verified.",
    },
    {
      icon: "💡",
      title: "Innovation",
      description: "We use technology to make healthcare more accessible, convenient, and efficient for everyone.",
    },
    {
      icon: "❤️",
      title: "Compassion",
      description: "We approach healthcare with empathy, understanding, and a genuine desire to help people heal.",
    },
  ];

  const stats = [
    {
      value: "10,000+",
      label: "Caregivers",
      description: "Verified professionals",
    },
    {
      value: "50,000+",
      label: "Patients Served",
      description: "Since platform launch",
    },
    {
      value: "4.8/5",
      label: "Average Rating",
      description: "From patient reviews",
    },
    {
      value: "50+",
      label: "Cities",
      description: "Across the United States",
    },
  ];

  return (
    <PageLayout
      title="About Careva"
      subtitle="We're on a mission to make quality healthcare accessible to everyone by connecting patients with qualified, verified caregivers."
    >
      {/* Mission */}
      <ContentSection
        title="Our Mission"
        subtitle="To revolutionize healthcare delivery by creating a trusted platform that connects patients with qualified caregivers, making quality care accessible, convenient, and affordable for everyone."
        backgroundColor={theme.palette.grey[50]}
      >
        <Box sx={{ textAlign: 'center', maxWidth: '800px', mx: 'auto' }}>
          <Typography
            variant="h6"
            sx={{
              color: theme.palette.text.secondary,
              lineHeight: 1.6,
              mb: 4,
            }}
          >
            Founded in 2020, Careva was born from a simple belief: everyone deserves access to quality healthcare. 
            We've built a platform that makes it easy for patients to find qualified caregivers and for healthcare 
            professionals to build successful practices.
          </Typography>
        </Box>
      </ContentSection>

      {/* Values */}
      <ContentSection
        title="Our Values"
        subtitle="These core values guide everything we do and shape how we serve our community."
      >
        <Grid container spacing={4}>
          {values.map((value, index) => (
            <Grid item xs={12} sm={6} md={3} key={index}>
              <Box sx={{ textAlign: 'center' }}>
                <Box
                  sx={{
                    fontSize: '3rem',
                    mb: 2,
                  }}
                >
                  {value.icon}
                </Box>
                <Typography
                  variant="h6"
                  sx={{
                    fontWeight: 'bold',
                    mb: 1,
                    color: theme.palette.text.primary,
                  }}
                >
                  {value.title}
                </Typography>
                <Typography
                  variant="body2"
                  sx={{
                    color: theme.palette.text.secondary,
                    lineHeight: 1.6,
                  }}
                >
                  {value.description}
                </Typography>
              </Box>
            </Grid>
          ))}
        </Grid>
      </ContentSection>

      {/* Stats */}
      <ContentSection
        title="Our Impact"
        subtitle="Numbers that reflect our commitment to improving healthcare access and quality."
        backgroundColor={theme.palette.grey[50]}
      >
        <StatsDisplay stats={stats} />
      </ContentSection>

      {/* CTA Section */}
      <CTASection
        title="Join Our Mission"
        subtitle="Whether you're a patient seeking care or a caregiver looking to make a difference, we'd love to have you join our community."
        primaryButton={{
          text: "Get Started",
          href: "/auth",
        }}
        secondaryButton={{
          text: "Contact Us",
          href: "/contact",
          variant: "outlined",
        }}
      />
    </PageLayout>
  );
};
